/**
 * AddToPlaylist — ⭐ **〔加入播放清單〕彈窗**（0013；`decisions/033`；產品負責人 2026-09-24）
 *
 * 產品負責人原話：「one more button beside sharing, name it **add to playlist**, then pop up the
 *   catagory to add in」＋「the playlist … plays one after one」（分類就係播放清單）
 *
 * 責任（硬規則 A：頁面唔准寫底層邏輯 —— 所有網絡動作喺 `usePlaylists()`）：
 *   · 打開 → 讀「呢首歌入咗邊幾個清單」（讀唔到 → **照實講**，唔准當「冇加入」）
 *   · 撳 chip → 加／移出（後端 idempotent；`added:false`／`removed:false` **照實**出字）
 *   · 可以順手開一個新清單（≤20 字）—— 開完即刻加入呢首歌
 *   · ⚠️ **唔准自動幫用戶分類**（唔用 AI `mood_tags`）：分類係用戶嘅表達
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BottomSheet from '@coeldery/bottom-sheet';
import { playlistLabel } from '../playlists';
import { usePlaylists, type Playlist } from '../hooks/usePlaylists';

export interface AddToPlaylistProps {
  open: boolean;
  /** 要加入嘅歌（`null` ＝ 未有） */
  songId: string | null;
  onClose: () => void;
  /** 加／移完之後通知父層（例：更新首數） */
  onChanged?: () => void;
}

const INPUT_STYLE: React.CSSProperties = {
  flex: 1,
  minHeight: '42px',
  padding: '8px 12px',
  borderRadius: '12px',
  border: '1.5px solid var(--color-divider)',
  background: 'var(--color-card)',
  color: 'var(--color-text)',
  fontFamily: 'inherit',
  fontSize: 'var(--font-size-small, 15px)',
};

export default function AddToPlaylist({ open, songId, onClose, onChanged }: AddToPlaylistProps) {
  const { t } = useTranslation();
  const lib = usePlaylists();
  /** `null` ＝ 未讀到（⚠️ 唔可以當「冇加入」） */
  const [memberIds, setMemberIds] = useState<string[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [working, setWorking] = useState<string | null>(null);
  const { memberships } = lib;

  /** 打開（或換歌）→ 讀已加入嘅清單；讀唔到 → 照實講（唔准扮「冇加入」） */
  useEffect(() => {
    if (!open || !songId) return;
    let alive = true;
    setNote(null);
    setMemberIds(null);
    void (async () => {
      const ids = await memberships(songId);
      if (!alive) return;
      if (ids === null) {
        setMemberIds([]);
        setNote(t('playlist_membership_failed'));
        return;
      }
      setMemberIds(ids);
    })();
    return () => {
      alive = false;
    };
  }, [memberships, open, songId, t]);

  /** 撳 chip ＝ 加／移出（**唔准**靜靜當成功：`added:false`／`removed:false` 都要講） */
  const toggle = useCallback(
    async (pl: Playlist) => {
      if (!songId) return;
      const on = (memberIds ?? []).includes(pl.id);
      const label = playlistLabel(pl, t);
      setWorking(pl.id);
      /** ⚠️ 分開兩個分支（唔用 union）：`added`／`removed` 係唔同形狀，混埋一齊型別會唔清 */
      if (on) {
        const res = await lib.removeSong(pl.id, songId);
        setWorking(null);
        if (!res.ok) {
          setNote(t('playlist_action_failed', { error: res.error ?? '' }));
          return;
        }
        if (res.removed === false) {
          setNote(t('playlist_not_in_note'));
          return;
        }
        setMemberIds((prev) => (prev ?? []).filter((x) => x !== pl.id));
        setNote(t('playlist_removed_note', { name: label }));
      } else {
        const res = await lib.addSong(pl.id, songId);
        setWorking(null);
        if (!res.ok) {
          setNote(t('playlist_action_failed', { error: res.error ?? '' }));
          return;
        }
        if (res.added === false) {
          setNote(t('playlist_already_note', { name: label }));
          return;
        }
        setMemberIds((prev) => [...(prev ?? []), pl.id]);
        setNote(t('playlist_added_note', { name: label }));
      }
      onChanged?.();
    },
    [lib, memberIds, onChanged, songId, t],
  );

  /** 開新清單（開完即刻加入呢首歌） */
  const create = useCallback(async () => {
    const name = newName.trim();
    if (!name) return;
    setWorking('__new__');
    const res = await lib.createCustom(name);
    if (!res.ok) {
      setWorking(null);
      setNote(t('playlist_new_failed', { error: res.error ?? '' }));
      return;
    }
    const id = res.playlist?.id;
    if (id && songId) {
      const added = await lib.addSong(id, songId);
      if (added.ok) setMemberIds((prev) => [...(prev ?? []), id]);
    }
    setWorking(null);
    setNewName('');
    setNote(t('playlist_added_note', { name }));
    onChanged?.();
  }, [lib, newName, onChanged, songId, t]);


  return (
    <BottomSheet
      open={open}
      titleKey="playlist_sheet_title"
      onClose={onClose}
      actions={
        <button
          type="button"
          data-testid="playlist-done"
          onClick={onClose}
          className="lang-btn lang-btn--active"
          style={{ width: '100%', minHeight: '44px' }}
        >
          {t('playlist_done')}
        </button>
      }
    >
      <p className="footnote" style={{ margin: '0 0 10px' }}>
        {t('playlist_sheet_hint')}
      </p>
      <div data-testid="playlist-options" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {lib.playlists.map((pl) => {
          const on = (memberIds ?? []).includes(pl.id);
          return (
            <button
              key={pl.id}
              type="button"
              data-testid="playlist-option"
              aria-pressed={on}
              disabled={working !== null}
              onClick={() => void toggle(pl)}
              className={on ? 'lang-btn lang-btn--active' : 'lang-btn'}
            >
              {playlistLabel(pl, t)}
              {' · '}
              {t('playlist_count', { n: pl.count })}
            </button>
          );
        })}
      </div>
      {lib.hydrated && lib.playlists.length === 0 ? (
        <p className="footnote" role="alert" style={{ margin: '10px 0 0' }}>
          {t('playlist_membership_failed')}
          {lib.error ? `（${lib.error}）` : ''}
        </p>
      ) : null}

      {/* 新清單（產品負責人：「should able to add later」） */}
      <label
        htmlFor="playlist-new"
        className="footnote"
        style={{ display: 'block', fontWeight: 700, marginTop: '14px' }}
      >
        {t('playlist_new_label')}
      </label>
      <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
        <input
          id="playlist-new"
          data-testid="playlist-new-input"
          value={newName}
          maxLength={20}
          disabled={working !== null}
          placeholder={t('playlist_new_placeholder')}
          onChange={(e) => setNewName(e.target.value)}
          style={INPUT_STYLE}
        />
        <button
          type="button"
          data-testid="playlist-new-create"
          disabled={working !== null || newName.trim().length === 0}
          onClick={() => void create()}
          className="lang-btn lang-btn--active"
          style={{ minWidth: '64px' }}
        >
          {t('playlist_new_button')}
        </button>
      </div>

      {note ? (
        <p className="footnote" role="status" data-testid="playlist-note" style={{ margin: '10px 0 0' }}>
          {note}
        </p>
      ) : null}
    </BottomSheet>
  );
}
